import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO } from "../../lib/images";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Menu", to: "/menu" },
  { label: "About", to: "/about" },
  { label: "Contact Us", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || location.pathname !== "/";

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          solid ? "bg-hb-black/85 backdrop-blur-xl border-b border-hb-line" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO} alt="Hackberry Home" className="h-11 w-11 object-contain logo-invert" />
            <span className="hidden sm:block leading-none">
              <span className="font-serif text-xl tracking-wide text-hb-cream block">HACKBERRY HOME</span>
              <span className="eyebrow text-hb-brass text-[0.6rem]">Southern Scratch Made Kitchen</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`eyebrow text-[0.7rem] transition-colors duration-300 hover:text-hb-brass ${
                  location.pathname === l.to ? "text-hb-brass" : "text-hb-cream"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={() => navigate("/menu")}
              data-testid="nav-view-menu-button"
              className="btn-brass eyebrow text-[0.7rem] px-6 py-3"
            >
              View Menu
            </button>
          </div>

          <button
            className="md:hidden text-hb-cream p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-hamburger"
          >
            <Menu size={26} />
          </button>
        </nav>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu"
      >
        <div className="absolute inset-0 bg-hb-black/95 backdrop-blur-xl" onClick={() => setOpen(false)} />
        <div className="relative h-full flex flex-col px-8 py-6">
          <div className="flex items-center justify-between">
            <img src={LOGO} alt="Hackberry Home" className="h-12 w-12 object-contain logo-invert" />
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="text-hb-cream p-2">
              <X size={28} />
            </button>
          </div>
          <div className="flex-1 flex flex-col justify-center gap-2">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-serif text-5xl text-hb-cream py-2 border-b border-hb-line hover:text-hb-brass transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
          <button
            onClick={() => navigate("/menu")}
            className="btn-brass eyebrow py-4 w-full"
            data-testid="mobile-view-menu-button"
          >
            View Our Menu
          </button>
        </div>
      </div>
    </>
  );
};
